// Renderizador de Markdown SEGURO para a Central de Ajuda.
//
// Segurança: NUNCA usa dangerouslySetInnerHTML — o texto é convertido em elementos React (escapados pelo
// próprio React), então HTML/scripts embutidos no conteúdo NÃO são executados nem interpretados. Apenas um
// subconjunto é suportado (títulos, negrito, itálico, código, listas, tabelas, citação, regra, links e
// imagens). Links são restritos a destinos INTERNOS; imagens só resolvem por id de imagem do próprio artigo
// (servidas por endpoint com gate). Links/imagens externos viram texto simples.
import { Fragment, type ReactNode } from "react";

const UUID_RE = /^[0-9a-fA-F-]{36}$/;

export type MarkdownHandlers = {
  /** Resolve a URL (com gate) de uma imagem pelo id; retorne null para esconder. */
  resolveImageUrl?: (imageId: string) => string | null;
  /** Navegação interna da SPA (href iniciando com "/"). */
  onNavigate?: (path: string) => void;
  /** Abrir outro artigo pelo slug (link "artigo:slug" ou "help:slug"). */
  onOpenArticle?: (slug: string) => void;
};

type InlineKey = { k: number };

/** Tokeniza e renderiza texto inline (negrito/itálico/código/link/imagem). */
function renderInline(text: string, h: MarkdownHandlers, key: InlineKey): ReactNode[] {
  const out: ReactNode[] = [];
  let rest = text;
  // Ordem importa: imagem antes de link; negrito antes de itálico.
  const patterns: { re: RegExp; make: (m: RegExpExecArray) => ReactNode }[] = [
    {
      re: /!\[([^\]]*)\]\(([^)]+)\)/,
      make: (m) => {
        const alt = m[1];
        const src = m[2].trim();
        if (UUID_RE.test(src) && h.resolveImageUrl) {
          const url = h.resolveImageUrl(src);
          if (url) {
            return (
              <img
                key={key.k++}
                src={url}
                alt={alt}
                loading="lazy"
                className="my-2 max-h-[420px] w-auto max-w-full rounded-md border border-border"
              />
            );
          }
        }
        return <span key={key.k++} className="text-xs italic text-muted-foreground">[{alt || "imagem"}]</span>;
      },
    },
    {
      re: /\[([^\]]+)\]\(([^)]+)\)/,
      make: (m) => {
        const label = m[1];
        const href = m[2].trim();
        if (href.startsWith("/")) {
          return (
            <a
              key={key.k++}
              href={href}
              onClick={(e) => {
                e.preventDefault();
                h.onNavigate?.(href);
              }}
              className="font-medium text-primary underline underline-offset-2 hover:opacity-80"
            >
              {label}
            </a>
          );
        }
        const article = href.startsWith("artigo:") ? href.slice(7) : href.startsWith("help:") ? href.slice(5) : null;
        if (article) {
          return (
            <button
              key={key.k++}
              type="button"
              onClick={() => h.onOpenArticle?.(article)}
              className="font-medium text-primary underline underline-offset-2 hover:opacity-80"
            >
              {label}
            </button>
          );
        }
        // Destino externo/não suportado: vira texto simples (não vira link).
        return <span key={key.k++}>{label}</span>;
      },
    },
    { re: /\*\*([^*]+)\*\*/, make: (m) => <strong key={key.k++}>{m[1]}</strong> },
    { re: /`([^`]+)`/, make: (m) => (
      <code key={key.k++} className="rounded bg-muted px-1 py-0.5 font-mono text-[0.85em]">{m[1]}</code>
    ) },
    { re: /(?:\*([^*]+)\*|_([^_]+)_)/, make: (m) => <em key={key.k++}>{m[1] ?? m[2]}</em> },
  ];

  while (rest.length > 0) {
    let best: { index: number; length: number; node: ReactNode } | null = null;
    for (const p of patterns) {
      const m = p.re.exec(rest);
      if (m && (best === null || m.index < best.index)) {
        best = { index: m.index, length: m[0].length, node: p.make(m) };
      }
    }
    if (!best) {
      out.push(<Fragment key={key.k++}>{rest}</Fragment>);
      break;
    }
    if (best.index > 0) {
      out.push(<Fragment key={key.k++}>{rest.slice(0, best.index)}</Fragment>);
    }
    out.push(best.node);
    rest = rest.slice(best.index + best.length);
  }
  return out;
}

function isTableSeparator(line: string): boolean {
  return /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|?\s*$/.test(line);
}

function splitRow(line: string): string[] {
  return line.replace(/^\s*\|/, "").replace(/\|\s*$/, "").split("|").map((c) => c.trim());
}

/** Início de um novo bloco (título, item de lista, citação ou regra) — encerra agregação de lista/parágrafo. */
function isBlockStart(line: string): boolean {
  return /^(#{1,4}\s|[-*]\s|\d+\.\s|>|---+$)/.test(line);
}

/** Renderiza o Markdown (subconjunto) como elementos React. */
export function HelpMarkdown({ content, handlers = {} }: { content: string; handlers?: MarkdownHandlers }) {
  const lines = (content ?? "").replace(/\r\n/g, "\n").split("\n");
  const blocks: ReactNode[] = [];
  const key: InlineKey = { k: 0 };
  let i = 0;

  const pushList = (ordered: boolean, items: string[]) => {
    const children = items.map((it, idx) => <li key={idx} className="ml-1">{renderInline(it, handlers, key)}</li>);
    blocks.push(
      ordered
        ? <ol key={key.k++} className="my-2 list-decimal space-y-1 pl-6">{children}</ol>
        : <ul key={key.k++} className="my-2 list-disc space-y-1 pl-6">{children}</ul>,
    );
  };

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    if (trimmed === "") { i++; continue; }

    if (/^---+$/.test(trimmed)) {
      blocks.push(<hr key={key.k++} className="my-4 border-border" />);
      i++;
      continue;
    }

    const heading = /^(#{1,4})\s+(.*)$/.exec(trimmed);
    if (heading) {
      const level = heading[1].length;
      const text = heading[2];
      const cls = level === 1 ? "mt-1 mb-3 text-xl font-bold"
        : level === 2 ? "mt-4 mb-2 text-lg font-semibold"
        : level === 3 ? "mt-3 mb-1 text-base font-semibold"
        : "mt-2 mb-1 text-sm font-semibold";
      const Tag = (`h${Math.min(level + 1, 6)}`) as keyof JSX.IntrinsicElements;
      blocks.push(<Tag key={key.k++} className={cls}>{renderInline(text, handlers, key)}</Tag>);
      i++;
      continue;
    }

    if (trimmed.startsWith(">")) {
      const raw: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith(">")) {
        raw.push(lines[i].trim().replace(/^>\s?/, ""));
        i++;
      }
      // Agrupa em parágrafos: linhas consecutivas não vazias juntam (frase quebrada em várias linhas);
      // uma linha ">" vazia separa parágrafos.
      const paras: string[] = [];
      let cur: string[] = [];
      for (const q of raw) {
        if (q.trim() === "") {
          if (cur.length) { paras.push(cur.join(" ")); cur = []; }
        } else {
          cur.push(q.trim());
        }
      }
      if (cur.length) paras.push(cur.join(" "));
      blocks.push(
        <blockquote key={key.k++} className="my-3 border-l-4 border-primary/40 bg-muted/40 py-2 pl-4 text-sm">
          {paras.map((q, idx) => <p key={idx} className="[&:not(:first-child)]:mt-1">{renderInline(q, handlers, key)}</p>)}
        </blockquote>,
      );
      continue;
    }

    if (/^[-*]\s+/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length) {
        const t = lines[i].trim();
        if (/^[-*]\s+/.test(t)) {
          items.push(t.replace(/^[-*]\s+/, ""));
          i++;
        } else if (t !== "" && !isBlockStart(t)) {
          // Linha de continuação (item quebrado em várias linhas): junta ao item atual.
          items[items.length - 1] += " " + t;
          i++;
        } else {
          break;
        }
      }
      pushList(false, items);
      continue;
    }

    if (/^\d+\.\s+/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length) {
        const t = lines[i].trim();
        if (/^\d+\.\s+/.test(t)) {
          items.push(t.replace(/^\d+\.\s+/, ""));
          i++;
        } else if (t !== "" && !isBlockStart(t)) {
          items[items.length - 1] += " " + t;
          i++;
        } else {
          break;
        }
      }
      pushList(true, items);
      continue;
    }

    // Tabela: linha com "|" seguida de linha separadora.
    if (trimmed.includes("|") && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
      const header = splitRow(trimmed);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && lines[i].trim().includes("|") && lines[i].trim() !== "") {
        rows.push(splitRow(lines[i].trim()));
        i++;
      }
      blocks.push(
        <div key={key.k++} className="my-3 overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>{header.map((c, idx) => (
                <th key={idx} className="border border-border bg-muted/50 px-2 py-1 text-left font-semibold">
                  {renderInline(c, handlers, key)}
                </th>
              ))}</tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri}>{r.map((c, ci) => (
                  <td key={ci} className="border border-border px-2 py-1 align-top">{renderInline(c, handlers, key)}</td>
                ))}</tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    // Parágrafo: agrega linhas consecutivas até uma vazia.
    const para: string[] = [];
    while (i < lines.length && lines[i].trim() !== "" && !/^(#{1,4}\s|[-*]\s|\d+\.\s|>|---+$)/.test(lines[i].trim())) {
      para.push(lines[i].trim());
      i++;
    }
    blocks.push(<p key={key.k++} className="my-2 leading-relaxed">{renderInline(para.join(" "), handlers, key)}</p>);
  }

  return <div className="text-sm text-foreground">{blocks}</div>;
}
